import * as SliderPrimitive from "@radix-ui/react-slider";
import { memo, useCallback } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import * as styles from "./redo-range-slider.module.css";

export interface RedoRangeSliderProps {
  /** Label shown on the left side of the slider */
  leftLabel?: string;
  /** Label shown on the right side of the slider */
  rightLabel?: string;
  /** Current value as [min, max] tuple */
  value: [number, number];
  /** Callback when value changes */
  onChange: (value: [number, number]) => void;
  /** Minimum value (default: 1) */
  min?: number;
  /** Maximum value (default: 10) */
  max?: number;
  /** Step increment (default: 1) */
  step?: number;
  /** Whether the slider is disabled */
  disabled?: boolean;
}

/**
 * A dual-thumb range slider for selecting a min/max range.
 *
 * Figma: https://www.figma.com/design/iZHj2I36zd9i8nRbWKw4ZK/%E2%9D%96-Arbiter
 */
export const RedoRangeSlider = memo(function RedoRangeSlider({
  leftLabel,
  rightLabel,
  value,
  onChange,
  min = 1,
  max = 10,
  step = 1,
  disabled = false,
}: RedoRangeSliderProps) {
  const handleValueChange = useCallback(
    (newValue: number[]) => {
      if (newValue.length >= 2) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        onChange([newValue[0], newValue[1]]);
      }
    },
    [onChange],
  );

  return (
    <Flex data-arbiter="RedoRangeSlider" dir="column" gap="sm" w="full">
      <SliderPrimitive.Root
        className={styles.sliderRoot}
        disabled={disabled}
        max={max}
        min={min}
        onValueChange={handleValueChange}
        step={step}
        value={value}
      >
        <SliderPrimitive.Track className={styles.sliderTrack}>
          <SliderPrimitive.Range className={styles.sliderRange} />
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb className={styles.sliderThumb} />
        <SliderPrimitive.Thumb className={styles.sliderThumb} />
      </SliderPrimitive.Root>
      {(leftLabel || rightLabel) && (
        <Flex dir="row" justify="space-between" w="full">
          <Text fontSize="xs" textColor="tertiary">
            {leftLabel}
          </Text>
          <Text fontSize="xs" textColor="tertiary">
            {rightLabel}
          </Text>
        </Flex>
      )}
    </Flex>
  );
});
